import { Add, InfoOutlined } from '@mui/icons-material';
import { Button, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'src/constants/paths';
import {
  TEMP_STORED_PRODUCT_DATA,
  TEMP_STORED_PRODUCT_FILE,
} from 'src/features/products/editProductPage/hooks/EditProductPage.helper';
import {
  IProductVariation,
  IProductVariationFlavor,
  IProductVariationSize,
} from 'src/interfaces';
import { lang } from 'src/utils';

import { InputWidget } from '../widget';
import { DyDisplayTooltip } from './ProductVariationDyDisplay.styled';
import { TemplateVariationMoreOptions } from './TemplateVariationMoreOptions';
import { TemplateVariations } from './TemplateVariations';

export const ProductVariationDyDisplay = ({
  productVariation,
  productVariationTypes,
  productVariationFlavors,
  onDeleteDyDisplay,
  isAddDyDisplayDisabled = false,
  onBeforeNavigate,
}: {
  productVariation?: IProductVariation | null;
  productVariationTypes: IProductVariationSize[];
  productVariationFlavors: IProductVariationFlavor[];
  onDeleteDyDisplay: () => void;
  isAddDyDisplayDisabled?: boolean;
  onBeforeNavigate?: () => void;
}) => {
  const navigate = useNavigate();
  const formContext = useFormContext();
  const nameOverride = formContext.watch('name_override');
  const productCode = productVariation?.group_product_code || '';
  const variationCode = productVariation?.product_code || '';
  const hasDyTemplate = !!productVariation?.variation_template;

  const isDyDisplayDisabled =
    isAddDyDisplayDisabled || !variationCode || !productCode;

  const handleAddDyDisplay = async () => {
    onBeforeNavigate?.();
    // Temporarily store the form values
    sessionStorage.setItem(
      TEMP_STORED_PRODUCT_DATA(productCode),
      JSON.stringify(formContext.getValues()),
    );

    // Save asset separately if it's a File
    const asset = formContext.getValues('asset');
    if (asset instanceof File) {
      const dataURL = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(asset);
      });
      sessionStorage.setItem(
        TEMP_STORED_PRODUCT_FILE(productCode),
        JSON.stringify({
          dataURL,
          name: asset.name,
          type: asset.type,
          size: asset.size,
          lastModified: asset.lastModified,
        }),
      );
    }

    if (variationCode && productCode) {
      navigate(
        PATHS.PRODUCT_VARIATION_TEMPLATE_ADD(productCode, variationCode),
      );
    }
  };

  return (
    <Grid container>
      <Grid item xs={12} paddingBottom={2}>
        <Typography variant="h4" fontWeight="400">
          {lang.dyDisplay}

          {isDyDisplayDisabled && (
            <DyDisplayTooltip
              title={lang.saveThePageToEnableDyDisplay}
              arrow
              placement="top">
              <IconButton size="small" disableRipple>
                <InfoOutlined fontSize="small" />
              </IconButton>
            </DyDisplayTooltip>
          )}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        {hasDyTemplate ? (
          <Grid
            container
            alignItems={'center'}
            justifyContent={'space-between'}>
            <Grid item xs={2} height={'stretch'}>
              <Typography variant="body1" fontWeight="400" paddingBottom={2}>
                {lang.template}
              </Typography>
              <Typography variant="body1" fontWeight="400">
                {productVariation?.variation_template?.template_name?.replace(
                  '_',
                  ' ',
                )}
              </Typography>
            </Grid>

            <TemplateVariations
              nameOverride={nameOverride}
              productVariation={productVariation}
              productVariationTypes={productVariationTypes}
              productVariationFlavors={productVariationFlavors}
              renderItem={(value, order) => (
                <Tooltip title={value} placement="bottom" arrow>
                  <span>
                    <InputWidget
                      label={`Product ${order}`}
                      disabled={true}
                      onChange={() => {}}
                      value={value}
                    />
                  </span>
                </Tooltip>
              )}
            />

            <TemplateVariationMoreOptions
              productVariation={productVariation}
              onDeleteDyDisplay={onDeleteDyDisplay}
              onBeforeNavigate={onBeforeNavigate}
            />
          </Grid>
        ) : (
          <Button
            variant="outlined"
            startIcon={<Add />}
            disabled={isDyDisplayDisabled}
            onClick={handleAddDyDisplay}>
            {lang.addDyDisplay}
          </Button>
        )}
      </Grid>
    </Grid>
  );
};
